/**
 * Live Quiz Display - Web Presentation Screen
 * This is the display shown on a TV/projector during live quiz sessions
 * Controlled by the family admin via their phone
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  View,
  Text,
  Image,
  Platform,
  Dimensions,
  Animated,
  Easing,
  StyleSheet,
  ViewStyle,
  TextStyle,
  ImageStyle,
} from "react-native";
import { useLocalSearchParams } from "expo-router";
import { supabase } from "@/lib/supabase/client";
import { LinearGradient } from "expo-linear-gradient";
import LottieView from "lottie-react-native";
import { getLottieByKey, getLottieSource, LOTTIE_REGISTRY } from "@/lib/lottie-registry";
import {
  LiveQuizSession,
  LiveQuizParticipant,
  Quiz,
  QuizQuestion,
  RankingEntry,
  QuestionResult,
  QuestionPhase,
  ParticipantAnswerResult,
} from "@/modules/quiz-live";

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get("window");

/**
 * Quiz Display Screen
 */
export default function QuizDisplayScreen() {
  const params = useLocalSearchParams<{ sessionId?: string }>();
  const sessionId = params.sessionId;
  
  // State
  const [session, setSession] = useState<LiveQuizSession | null>(null);
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [participants, setParticipants] = useState<LiveQuizParticipant[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<QuizQuestion | null>(null);
  const [questionResult, setQuestionResult] = useState<QuestionResult | null>(null);
  const [participantResults, setParticipantResults] = useState<ParticipantAnswerResult[]>([]);
  const [rankings, setRankings] = useState<RankingEntry[]>([]);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Question phase state
  const [questionPhase, setQuestionPhase] = useState<QuestionPhase>("active");
  const [countdownSeconds, setCountdownSeconds] = useState(0);
  // After results have been shown for a few seconds, auto-transition to the
  // ranking view so the audience sees the updated standings (Kahoot-style).
  const [showRankingAfterResults, setShowRankingAfterResults] = useState(false);
  const rankingTransitionRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Animation values
  const timerAnim = useRef(new Animated.Value(1)).current;
  const questionSlideAnim = useRef(new Animated.Value(0)).current;
  const optionAnims = useRef([
    new Animated.Value(0),
    new Animated.Value(0),
    new Animated.Value(0),
    new Animated.Value(0),
  ]).current;
  
  // Answer reveal state
  const [answerRevealed, setAnswerRevealed] = useState(false);
  
  // Refs
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const subscribedSessionIdRef = useRef<string | null>(null);
  const quizRef = useRef<Quiz | null>(null);
  const sessionRef = useRef<LiveQuizSession | null>(null);
  const currentQuestionRef = useRef<QuizQuestion | null>(null);
  const resultsTriggeredRef = useRef<string | null>(null);
  
  // Keep refs updated
  useEffect(() => {
    quizRef.current = quiz;
  }, [quiz]);
  
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);
  
  useEffect(() => {
    currentQuestionRef.current = currentQuestion;
  }, [currentQuestion]);
  
  /**
   * Fetch session data
   */
  const fetchSession = useCallback(async () => {
    if (!sessionId) {
      return;
    }
    
    
    const { data, error: fetchError } = await supabase
      .from("quiz_live_sessions")
      .select(`
        *,
        quiz:quizzes (
          *,
          questions:quiz_questions (*)
        )
      `)
      .eq("id", sessionId)
      .single();
    
    if (fetchError) {
      console.error("[QuizDisplay] Session fetch error:", fetchError);
      setError("Sessie niet gevonden");
      return;
    }
    
    setSession(data as unknown as LiveQuizSession);
    setQuiz(data.quiz as unknown as Quiz);
    
    // Fetch participants
    const { data: participantsData, error: participantsError } = await supabase
      .from("quiz_live_participants")
      .select(`
        *,
        team:teams (id, name, color, avatar_url),
        player:profiles!player_user_id (id, full_name, nickname, avatar_url)
      `)
      .eq("session_id", sessionId);
    
    setParticipants((participantsData || []) as unknown as LiveQuizParticipant[]);
    
    // Get current question if playing
    if (data.status === "playing" && data.quiz?.questions) {
      const questionId = data.questions_order[data.current_question_index];
      const question = data.quiz.questions.find((q: QuizQuestion) => q.id === questionId);
      setCurrentQuestion(question || null);
      setQuestionPhase("active");
      // Timer will be started by the useEffect that watches session.question_ends_at
    }
    
    // Calculate rankings (inline to avoid dependency issue)
    const sorted = [...(participantsData || [])]
      .sort((a, b) => (b.total_score || 0) - (a.total_score || 0))
      .map((participant, index) => ({
        rank: index + 1,
        participant: participant as unknown as LiveQuizParticipant,
        total_score: participant.total_score || 0,
        correct_answers: participant.correct_answers || 0,
        average_time_ms: 0,
      }));
    setRankings(sorted);
  }, [sessionId]);
  
  /**
   * Update rankings from participants
   */
  const updateRankings = useCallback((participantsList: LiveQuizParticipant[]) => {
    const sorted = [...participantsList]
      .sort((a, b) => b.total_score - a.total_score)
      .map((participant, index) => ({
        rank: index + 1,
        participant,
        total_score: participant.total_score,
        correct_answers: participant.correct_answers,
        average_time_ms: 0,
        mascotLottie: getLottieByKey(participant.mascot_key),
      }));
    
    setRankings(sorted);
  }, []);
  
  /**
   * Fetch participants (separate function for refetching)
   */
  const fetchParticipantsData = useCallback(async () => {
    if (!sessionId) return;
    const { data } = await supabase
      .from("quiz_live_participants")
      .select(`
        *,
        team:teams (id, name, color, avatar_url),
        player:profiles!player_user_id (id, full_name, nickname, avatar_url)
      `)
      .eq("session_id", sessionId);
    
    setParticipants((data || []) as unknown as LiveQuizParticipant[]);
    updateRankings(data || []);
  }, [sessionId, updateRankings]);
  
  /**
   * Trigger auto-show results via Edge Function
   * This is idempotent - calling it multiple times has no effect
   */
  const triggerShowResults = useCallback(async () => {
    const currentSession = sessionRef.current;
    const question = currentQuestionRef.current;
    
    if (!currentSession?.id || !question?.id || currentSession.status === "paused") {
      return;
    }
    
    // Check if we already triggered for this question
    if (resultsTriggeredRef.current === question.id) {
      return;
    }
    
    // Mark as triggered immediately (optimistic)
    resultsTriggeredRef.current = question.id;
    
    
    try {
      const anonKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY || process.env.EXPO_PUBLIC_SUPABASE_KEY || "";
      const response = await fetch(
        `${process.env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/quiz-show-results`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${anonKey}`,
            "apikey": anonKey,
          },
          body: JSON.stringify({
            session_id: currentSession.id,
            question_id: question.id,
          }),
        }
      );
      
      const result = await response.json();
      
      if (result.success) {
      } else if (result.already_shown) {
      } else if (result.error) {
        console.error("[QuizDisplay] Edge Function error:", result.error);
        resultsTriggeredRef.current = null;
      }
    } catch (err) {
      console.error("[QuizDisplay] Failed to call Edge Function:", err);
      resultsTriggeredRef.current = null;
    }
  }, []);
  
  /**
   * Start countdown timer
   */
  const startTimer = useCallback((endsAt: string) => {
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    // Reset the results triggered flag for new question
    resultsTriggeredRef.current = null;
    
    const endTime = new Date(endsAt).getTime();
    const totalDuration = endTime - Date.now();
    
    // Reset timer animation
    timerAnim.setValue(1);
    Animated.timing(timerAnim, {
      toValue: 0,
      duration: totalDuration,
      easing: Easing.linear,
      useNativeDriver: false,
    }).start();
    
    timerRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((endTime - Date.now()) / 1000));
      setTimeRemaining(remaining);
      
      if (remaining <= 0 && timerRef.current) {
        clearInterval(timerRef.current);
        // Automatically trigger results when timer ends
        triggerShowResults();
      }
    }, 100);
  }, [timerAnim, triggerShowResults]);
  
  /**
   * Run 5-4-3-2-1 countdown locally from a server-set start timestamp.
   * Admin sets countdown_started_at slightly in the future to absorb network
   * latency; clamping elapsed to 0 ensures we always start the visible countdown
   * at the full COUNTDOWN_SECONDS even if we receive the event very quickly.
   */
  const startCountdownTimer = useCallback((countdownStartedAt: string) => {
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);

    const startTime = new Date(countdownStartedAt).getTime();
    const COUNTDOWN_SECONDS = 5;

    const updateTimer = () => {
      const elapsed = Math.max(0, Math.floor((Date.now() - startTime) / 1000));
      const remaining = Math.max(0, COUNTDOWN_SECONDS - elapsed);
      setCountdownSeconds(remaining);

      if (remaining <= 0 && countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };

    updateTimer();
    countdownTimerRef.current = setInterval(updateTimer, 100);
  }, []);

  /**
   * Animate question entrance
   */
  const animateQuestionEntrance = useCallback(() => {
    questionSlideAnim.setValue(0);
    optionAnims.forEach(anim => anim.setValue(0));
    
    // Question slides in
    Animated.spring(questionSlideAnim, {
      toValue: 1,
      tension: 50,
      friction: 7,
      useNativeDriver: true,
    }).start();
    
    // Options stagger in
    optionAnims.forEach((anim, index) => {
      setTimeout(() => {
        Animated.spring(anim, {
          toValue: 1,
          tension: 50,
          friction: 7,
          useNativeDriver: true,
        }).start();
      }, 200 + index * 100);
    });
  }, [questionSlideAnim, optionAnims]);
  
  /**
   * Subscribe to realtime updates
   */
  const subscribeToSession = useCallback(() => {
    if (!sessionId) return;
    
    // Check if already subscribed to this session
    if (subscribedSessionIdRef.current === sessionId) {
      return;
    }
    
    // Mark as subscribing FIRST
    subscribedSessionIdRef.current = sessionId;
    
    // Clean up existing channel if any
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    
    // Subscribe to admin channel for broadcasts (same as where admin/edge function sends)
    const channel = supabase.channel(`quiz_live_admin:${sessionId}`);
    
    // Session updates
    channel.on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "quiz_live_sessions",
        filter: `id=eq.${sessionId}`,
      },
      async (payload) => {
        setIsConnected(true);
        const newSession = payload.new as LiveQuizSession;
        const oldSession = payload.old as Partial<LiveQuizSession>;
        setSession(newSession);

        // Update current question from quiz data
        const currentQuiz = quizRef.current;
        if (newSession.status === "playing" && currentQuiz?.questions) {
          const questionId = newSession.questions_order[newSession.current_question_index];
          const question = currentQuiz.questions.find(q => q.id === questionId);
          setCurrentQuestion(question || null);
        }

        if (newSession.status === "completed" || newSession.status === "cancelled") {
          return;
        }

        // Pause: freeze the question timer + animation at the stored remaining value
        if (newSession.status === "paused" && oldSession.status !== "paused") {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          timerAnim.stopAnimation();
          if (newSession.time_remaining_when_paused != null) {
            setTimeRemaining(newSession.time_remaining_when_paused);
          }
        }

        // Resume: restart the question timer from the new deadline
        if (newSession.status === "playing" && oldSession.status === "paused") {
          if (newSession.question_ends_at && newSession.current_phase === "active") {
            startTimer(newSession.question_ends_at);
          }
        }

        // Reset per-question state when question advances
        if (newSession.current_question_index !== oldSession.current_question_index) {
          setQuestionResult(null);
          setParticipantResults([]);
          setAnswerRevealed(false);
        }

        // Drive UI phase from current_phase column
        switch (newSession.current_phase) {
          case "countdown":
            if (newSession.countdown_started_at && newSession.current_phase !== oldSession.current_phase) {
              setQuestionPhase("countdown");
              setQuestionResult(null);
              setParticipantResults([]);
              setAnswerRevealed(false);
              setShowRankingAfterResults(false);
              if (rankingTransitionRef.current) {
                clearTimeout(rankingTransitionRef.current);
                rankingTransitionRef.current = null;
              }
              startCountdownTimer(newSession.countdown_started_at);
            }
            break;
          case "active":
            if (newSession.current_phase !== oldSession.current_phase) {
              setQuestionPhase("active");
              setCountdownSeconds(0);
              setQuestionResult(null);
              setShowRankingAfterResults(false);
              if (rankingTransitionRef.current) {
                clearTimeout(rankingTransitionRef.current);
                rankingTransitionRef.current = null;
              }
              if (newSession.question_ends_at) {
                startTimer(newSession.question_ends_at);
              }
              animateQuestionEntrance();
            }
            break;
          case "results":
            if (newSession.current_phase !== oldSession.current_phase) {
              if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
              }
              setQuestionPhase("results");
              setShowRankingAfterResults(false);
              if (newSession.last_question_result) {
                setQuestionResult(newSession.last_question_result.results);
                setParticipantResults(newSession.last_question_result.participant_results ?? []);
              }
              // After 5s of results, transition to the ranking view (locally on
              // the web display). Admin still controls when to start next question.
              if (rankingTransitionRef.current) clearTimeout(rankingTransitionRef.current);
              rankingTransitionRef.current = setTimeout(() => {
                setShowRankingAfterResults(true);
              }, 5000);
            }
            break;
        }

        // Answer revealed mid-question
        if (newSession.answer_revealed && !oldSession.answer_revealed) {
          setAnswerRevealed(true);
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
        }
      }
    );
    
    // Participant updates
    channel.on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "quiz_live_participants",
        filter: `session_id=eq.${sessionId}`,
      },
      async () => {
        // If we receive data, we're connected (fallback)
        setIsConnected(true);
        const { data } = await supabase
          .from("quiz_live_participants")
          .select(`
            *,
            team:teams (id, name, color, avatar_url),
            player:profiles!player_user_id (id, full_name, nickname, avatar_url)
          `)
          .eq("session_id", sessionId);
        
        setParticipants((data || []) as unknown as LiveQuizParticipant[]);
        updateRankings(data || []);
      }
    );
    
    // Subscribe to channel
    channel.subscribe((status, err) => {
      if (status === "SUBSCRIBED") {
        setIsConnected(true);
        // Refetch current state on connection
        fetchParticipantsData();
      } else if (status === "CLOSED") {
        setIsConnected(false);
        channelRef.current = null;
        
        // Auto-reconnect after a short delay (use sessionRef for latest value)
        const currentSession = sessionRef.current;
        if (currentSession && (currentSession.status === "playing" || currentSession.status === "lobby")) {
          setTimeout(() => {
            subscribedSessionIdRef.current = null; // Clear so we can reconnect
            subscribeToSession();
          }, 1000);
        }
      } else if (status === "CHANNEL_ERROR") {
        console.error("[QuizDisplay] ⚠️ Channel error:", err);
        setIsConnected(false);
      }
    });
    
    channelRef.current = channel;
  }, [sessionId, startTimer, startCountdownTimer, animateQuestionEntrance, updateRankings, fetchParticipantsData]);
  
  // Initial load
  useEffect(() => {
    fetchSession();
  }, [fetchSession]);
  
  // Subscribe to updates - only resubscribe when sessionId changes
  useEffect(() => {
    if (sessionId) {
      subscribeToSession();
    }
    
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
      if (rankingTransitionRef.current) {
        clearTimeout(rankingTransitionRef.current);
        rankingTransitionRef.current = null;
      }
    };
  }, [sessionId, subscribeToSession]);
  
  // Sync UI phase from DB state -- runs on initial load AND when current_phase changes
  // Acts as a safety net if a postgres_changes Realtime event is missed.
  // Skips restarting timers/animations when the local phase already matches —
  // the postgres_changes handler is the primary path; this effect is just a backup.
  useEffect(() => {
    if (!session?.current_phase) return;

    if (session.current_phase === "countdown" && session.countdown_started_at) {
      if (questionPhase !== "countdown") {
        setQuestionPhase("countdown");
        startCountdownTimer(session.countdown_started_at);
      }
    } else if (session.current_phase === "active" && session.question_ends_at) {
      if (questionPhase !== "active") {
        setQuestionPhase("active");
        startTimer(session.question_ends_at);
        animateQuestionEntrance();

        const currentQuiz = quizRef.current;
        if (currentQuiz?.questions) {
          const questionId = session.questions_order[session.current_question_index];
          const question = currentQuiz.questions.find(q => q.id === questionId);
          setCurrentQuestion(question || null);
        }
      }
    } else if (session.current_phase === "results" && session.last_question_result) {
      if (questionPhase !== "results") {
        setQuestionPhase("results");
        setQuestionResult(session.last_question_result.results);
        setParticipantResults(session.last_question_result.participant_results ?? []);
        // On reconnect to a session already in "results", show ranking immediately
        // because the brief results-flash window has presumably already passed.
        setShowRankingAfterResults(true);
      }
    }

    if (session.answer_revealed && !answerRevealed) setAnswerRevealed(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id, session?.current_phase]);
  
  // Periodic session refresh — ONLY when disconnected.
  // Refreshing during connected "playing" causes a stale-read race condition where
  // an in-flight SELECT can resolve after a postgres_changes UPDATE event and
  // overwrite the latest state with stale data. Trust Realtime when connected.
  useEffect(() => {
    if (!sessionId || isConnected) return;

    const refreshInterval = setInterval(() => {
      fetchSession();
    }, 5000);

    return () => clearInterval(refreshInterval);
  }, [sessionId, isConnected, fetchSession]);

  // Per-participant connection status (< 4s online, 4-6s warning, > 6s offline)
  const participantConnections = useMemo((): Record<string, "online" | "warning" | "offline"> => {
    const result: Record<string, "online" | "warning" | "offline"> = {};
    const now = Date.now();
    for (const p of participants) {
      if (!p.last_seen_at) { result[p.id] = "offline"; continue; }
      const age = now - new Date(p.last_seen_at).getTime();
      if (age < 4000) result[p.id] = "online";
      else if (age < 6000) result[p.id] = "warning";
      else result[p.id] = "offline";
    }
    return result;
  }, [participants]);

  // Disconnect detection — same 2s poll / 5s threshold as admin, with 2-cycle debounce.
  // Web display can also write pause (conditional) so the quiz auto-pauses even if admin is gone.
  const disconnectStaleRef = useRef<Record<string, number>>({});
  const disconnectCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (session?.status !== "playing" || !sessionId) {
      if (disconnectCheckRef.current) { clearInterval(disconnectCheckRef.current); disconnectCheckRef.current = null; }
      disconnectStaleRef.current = {};
      return;
    }

    disconnectCheckRef.current = setInterval(async () => {
      const { data } = await supabase
        .from("quiz_live_participants")
        .select("id, last_seen_at, status")
        .eq("session_id", sessionId);
      if (!data) return;

      const now = Date.now();
      for (const p of data) {
        if (!p.last_seen_at) continue;
        const isStale = (now - new Date(p.last_seen_at).getTime()) > 5000;
        if (isStale && p.status !== "disconnected") {
          disconnectStaleRef.current[p.id] = (disconnectStaleRef.current[p.id] || 0) + 1;
          if (disconnectStaleRef.current[p.id] >= 2) {
            // Conditional: only pause if still playing (prevents race with admin)
            const endsAt = sessionRef.current?.question_ends_at
              ? new Date(sessionRef.current.question_ends_at).getTime() : now;
            const remainingSec = Math.max(0, Math.ceil((endsAt - now) / 1000));

            await supabase
              .from("quiz_live_sessions")
              .update({
                status: "paused",
                paused_at: new Date().toISOString(),
                paused_for_participant_id: p.id,
                time_remaining_when_paused: remainingSec,
              })
              .eq("id", sessionId)
              .eq("status", "playing");

            await supabase
              .from("quiz_live_participants")
              .update({ status: "disconnected" })
              .eq("id", p.id);

            disconnectStaleRef.current[p.id] = 0;
            break;
          }
        } else {
          disconnectStaleRef.current[p.id] = 0;
        }
      }
    }, 2000);

    return () => { if (disconnectCheckRef.current) clearInterval(disconnectCheckRef.current); };
  }, [session?.status, sessionId]);

  // Derive paused player name for the overlay
  const pausedPlayerName = useMemo(() => {
    if (session?.status !== "paused" || !session.paused_for_participant_id) return null;
    const p = participants.find(pp => pp.id === session.paused_for_participant_id);
    return p?.team?.name || p?.player?.full_name || "Onbekend";
  }, [session?.status, session?.paused_for_participant_id, participants]);

  // Only render on web
  if (Platform.OS !== "web") {
    return null;
  }

  // No session ID provided
  if (!sessionId) {
    return (
      <View style={styles.container}>
        <LinearGradient
          colors={["#1e1b4b", "#312e81", "#4c1d95"]}
          style={styles.gradient}
        >
          <Text style={styles.errorText}>
            Geen sessie ID opgegeven. Voeg ?sessionId=... toe aan de URL.
          </Text>
        </LinearGradient>
      </View>
    );
  }
  
  // Error state
  if (error) {
    return (
      <View style={styles.container}>
        <LinearGradient
          colors={["#1e1b4b", "#312e81", "#4c1d95"]}
          style={styles.gradient}
        >
          <Text style={styles.errorText}>{error}</Text>
        </LinearGradient>
      </View>
    );
  }
  
  // Loading
  if (!session || !quiz) {
    return (
      <View style={styles.container}>
        <LinearGradient
          colors={["#1e1b4b", "#312e81", "#4c1d95"]}
          style={styles.gradient}
        >
          <LottieView
            source={getLottieSource("confetti")}
            autoPlay
            loop
            style={{ width: 200, height: 200 }}
          />
          <Text style={styles.loadingText}>Quiz laden...</Text>
        </LinearGradient>
      </View>
    );
  }
  
  const quizColor = quiz.color || "#8B5CF6";
  
  return (
    <View style={styles.container}>
      <LinearGradient
        colors={["#1e1b4b", "#312e81", "#4c1d95"]}
        style={styles.gradient}
      >
        {/* Connection Status */}
        <View style={styles.statusBar}>
          <View style={[styles.connectionDot, { backgroundColor: isConnected ? "#10B981" : "#EF4444" }]} />
          <Text style={styles.statusText}>
            {isConnected ? "Verbonden" : "Verbinden..."}
          </Text>
        </View>
        
        {/* LOBBY STATE */}
        {session.status === "lobby" && (
          <LobbyView
            quiz={quiz}
            participants={participants}
            participantConnections={participantConnections}
          />
        )}
        
        {/* PLAYING STATE - Countdown */}
        {session.status === "playing" && questionPhase === "countdown" && (
          <CountdownView
            questionNumber={(session.current_question_index || 0) + 1}
            totalQuestions={session.total_questions}
            secondsRemaining={countdownSeconds}
            quizName={quiz.name}
            quizColor={quizColor}
          />
        )}
        
        {/* PLAYING STATE - Active Question */}
        {session.status === "playing" && questionPhase === "active" && currentQuestion && (
          <QuestionView
            question={currentQuestion}
            questionNumber={(session.current_question_index || 0) + 1}
            totalQuestions={session.total_questions}
            timeRemaining={timeRemaining}
            timerProgress={timerAnim}
            questionAnim={questionSlideAnim}
            optionAnims={optionAnims}
            result={questionResult}
            answerRevealed={answerRevealed}
            participants={participants}
            quizColor={quizColor}
          />
        )}
        
        {/* PLAYING STATE - Results (per-question breakdown, shown briefly) */}
        {session.status === "playing" && questionPhase === "results" && !showRankingAfterResults && currentQuestion && questionResult && (
          <ResultsView
            question={currentQuestion}
            questionNumber={(session.current_question_index || 0) + 1}
            totalQuestions={session.total_questions}
            result={questionResult}
            participantResults={participantResults}
            participants={participants}
            quizColor={quizColor}
          />
        )}

        {/* PLAYING STATE - Ranking (auto-shown after results) */}
        {session.status === "playing" && questionPhase === "results" && showRankingAfterResults && (
          <RankingView
            rankings={rankings}
            quizColor={quizColor}
          />
        )}
        
        {/* SHOWING RANKING */}
        {session.status === "showing_ranking" && (
          <RankingView
            rankings={rankings}
            quizColor={quizColor}
          />
        )}
        
        {/* COMPLETED */}
        {session.status === "completed" && (
          <FinalResultsView
            rankings={rankings}
            quizName={quiz.name}
            quizColor={quizColor}
          />
        )}

        {/* PAUSED OVERLAY */}
        {session.status === "paused" && pausedPlayerName && (
          <View style={styles.pauseOverlay}>
            <View style={styles.pauseCard}>
              <Text style={styles.pauseIcon}>⏸️</Text>
              <Text style={styles.pauseTitle}>Quiz Gepauzeerd</Text>
              <Text style={styles.pauseMessage}>
                Wachten op {pausedPlayerName}…
              </Text>
              <Text style={styles.pauseHint}>
                De timer is gestopt. Het spel gaat verder zodra iedereen weer verbonden is.
              </Text>
            </View>
          </View>
        )}
      </LinearGradient>
    </View>
  );
}

/**
 * Countdown View - Shows "Get ready for question X" with countdown
 */
interface CountdownViewProps {
  questionNumber: number;
  totalQuestions: number;
  secondsRemaining: number;
  quizName: string;
  quizColor: string;
}

const CountdownView: React.FC<CountdownViewProps> = ({
  questionNumber,
  totalQuestions,
  secondsRemaining,
  quizName,
  quizColor,
}) => {
  // Get countdown display color
  const getCountdownColor = () => {
    if (secondsRemaining === 3) return "#10B981"; // Green
    if (secondsRemaining === 2) return "#F59E0B"; // Yellow
    if (secondsRemaining === 1) return "#EF4444"; // Red
    return quizColor; // GO color
  };
  
  return (
    <View style={styles.countdownContainer}>
      <Text style={styles.countdownSubtitle}>
        Vraag {questionNumber} van {totalQuestions}
      </Text>
      <Text style={styles.countdownTitle}>
        {secondsRemaining <= 0 ? "🎯 DAAR GAAN WE!" : "Klaar voor de volgende vraag..."}
      </Text>
      <View style={[styles.countdownCircle, { backgroundColor: getCountdownColor() }]}>
        <Text style={styles.countdownNumber}>
          {secondsRemaining <= 0 ? "GO!" : secondsRemaining}
        </Text>
      </View>
    </View>
  );
};

/**
 * Results View - Shows who answered correctly after a question
 */
interface ResultsViewProps {
  question: QuizQuestion;
  questionNumber: number;
  totalQuestions: number;
  result: QuestionResult;
  participantResults: ParticipantAnswerResult[];
  participants: LiveQuizParticipant[];
  quizColor: string;
}

const ResultsView: React.FC<ResultsViewProps> = ({
  question,
  questionNumber,
  totalQuestions,
  result,
  participantResults,
  participants,
  quizColor,
}) => {
  const correctAnswer = question.options[result.correct_option_index];
  const fastestParticipant = result.fastest_correct
    ? participants.find(p => p.id === result.fastest_correct?.participant_id)
    : null;
  
  // Sort by correct first, then by time
  const sortedResults = [...participantResults].sort((a, b) => {
    if (a.isCorrect && !b.isCorrect) return -1;
    if (!a.isCorrect && b.isCorrect) return 1;
    return a.timeTakenMs - b.timeTakenMs;
  });
  
  return (
    <View style={styles.resultsContainer}>
      {/* Question indicator */}
      <Text style={styles.resultsSubtitle}>
        Vraag {questionNumber} van {totalQuestions}
      </Text>
      
      {/* Correct Answer */}
      <View style={styles.correctAnswerBox}>
        <Text style={styles.correctAnswerLabel}>✓ Het juiste antwoord is:</Text>
        <Text style={styles.correctAnswerText}>{correctAnswer?.text}</Text>
      </View>
      
      {/* Fastest Correct */}
      {result.fastest_correct && fastestParticipant && (
        <View style={styles.fastestBox}>
          <Text style={styles.fastestLabel}>⚡ Snelste juiste antwoord:</Text>
          <View style={styles.fastestInfo}>
            {fastestParticipant.mascot_key && (
              <View style={styles.fastestMascot}>
                {(() => {
                  const lottie = getLottieByKey(fastestParticipant.mascot_key);
                  return lottie?.source ? (
                    <LottieView
                      source={lottie.source}
                      autoPlay
                      loop
                      style={{ width: 80, height: 80 }}
                    />
                  ) : null;
                })()}
              </View>
            )}
            <View style={styles.fastestDetails}>
              <Text style={styles.fastestTeamName}>
                {fastestParticipant.team?.name}
              </Text>
              <Text style={styles.fastestTime}>
                {(result.fastest_correct.time_ms / 1000).toFixed(2)}s
              </Text>
            </View>
          </View>
        </View>
      )}
      
      {/* All Participant Results */}
      <View style={styles.allResultsBox}>
        <Text style={styles.allResultsTitle}>Alle antwoorden</Text>
        <View style={styles.resultsGrid}>
          {sortedResults.map((pResult, idx) => {
            const participant = participants.find(p => p.id === pResult.participantId);
            const mascotLottie = participant?.mascot_key
              ? getLottieByKey(participant.mascot_key)
              : null;
            
            return (
              <View
                key={pResult.participantId}
                style={[
                  styles.resultCard,
                  { backgroundColor: pResult.isCorrect ? "#10B98130" : "#EF444430" },
                ]}
              >
                <View
                  style={[
                    styles.resultIndicator,
                    { backgroundColor: pResult.isCorrect ? "#10B981" : "#EF4444" },
                  ]}
                >
                  <Text style={styles.resultIndicatorText}>
                    {pResult.isCorrect ? "✓" : "✗"}
                  </Text>
                </View>
                
                {mascotLottie?.source && (
                  <View style={styles.resultMascot}>
                    <LottieView
                      source={mascotLottie.source}
                      autoPlay
                      loop
                      style={{ width: 50, height: 50 }}
                    />
                  </View>
                )}
                
                <View style={styles.resultInfo}>
                  <Text style={styles.resultTeamName}>{pResult.teamName}</Text>
                  <Text style={styles.resultTime}>
                    {pResult.selectedOption !== null
                      ? `${(pResult.timeTakenMs / 1000).toFixed(2)}s`
                      : "Geen antwoord"
                    }
                  </Text>
                </View>
                
                <Text
                  style={[
                    styles.resultPoints,
                    { color: pResult.isCorrect ? "#10B981" : "#EF4444" },
                  ]}
                >
                  {pResult.isCorrect
                    ? `+${pResult.pointsEarned + pResult.bonusEarned}`
                    : "0"
                  }
                </Text>
              </View>
            );
          })}
        </View>
      </View>
    </View>
  );
};

/**
 * Lobby View - Shows teams waiting with their mascots
 */
interface LobbyViewProps {
  quiz: Quiz;
  participants: LiveQuizParticipant[];
  participantConnections?: Record<string, "online" | "warning" | "offline">;
}

const LobbyView: React.FC<LobbyViewProps> = ({ quiz, participants, participantConnections = {} }) => {
  const readyCount = participants.filter(p => p.is_ready).length;
  
  return (
    <View style={styles.lobbyContainer}>
      {/* Quiz Title */}
      <Text style={styles.quizTitle}>{quiz.name}</Text>
      <Text style={styles.quizSubtitle}>Wachten op teams...</Text>
      
      {/* Teams Grid */}
      <View style={styles.teamsGrid}>
        {participants.map((participant) => {
          const mascotLottie = getLottieByKey(participant.mascot_key);
          const conn = participantConnections[participant.id] || "offline";
          const dotColor = conn === "online" ? "#10B981" : conn === "warning" ? "#F59E0B" : "#EF4444";
          
          return (
            <View
              key={participant.id}
              style={[
                styles.teamCard,
                participant.is_ready && styles.teamCardReady,
              ]}
            >
              {/* Connection dot */}
              <View style={[styles.lobbyConnectionDot, { backgroundColor: dotColor }]} />

              {/* Mascot */}
              <View style={styles.mascotContainer}>
                {mascotLottie && (
                  <LottieView
                    source={mascotLottie.source}
                    autoPlay
                    loop
                    style={styles.mascotLottie}
                  />
                )}
              </View>
              
              {/* Team Name */}
              <Text style={styles.teamName} numberOfLines={2}>
                {participant.team?.name || "Team"}
              </Text>
              
              {/* Ready Badge */}
              {participant.is_ready && (
                <View style={styles.readyBadge}>
                  <Text style={styles.readyText}>✓ Klaar</Text>
                </View>
              )}
            </View>
          );
        })}
      </View>
      
      {/* Ready Counter */}
      <View style={styles.readyCounter}>
        <Text style={styles.readyCounterText}>
          {readyCount} / {participants.length} teams klaar
        </Text>
      </View>
    </View>
  );
};

/**
 * Question View - Shows question and options with timer
 */
interface QuestionViewProps {
  question: QuizQuestion;
  questionNumber: number;
  totalQuestions: number;
  timeRemaining: number;
  timerProgress: Animated.Value;
  questionAnim: Animated.Value;
  optionAnims: Animated.Value[];
  result: QuestionResult | null;
  answerRevealed?: boolean;
  participants: LiveQuizParticipant[];
  quizColor: string;
}

const QuestionView: React.FC<QuestionViewProps> = ({
  question,
  questionNumber,
  totalQuestions,
  timeRemaining,
  timerProgress,
  questionAnim,
  optionAnims,
  result,
  answerRevealed = false,
  participants,
  quizColor,
}) => {
  const OPTION_COLORS = ["#EF4444", "#3B82F6", "#F59E0B", "#10B981"];
  const OPTION_SHAPES = ["triangle", "diamond", "circle", "square"];
  
  const timerWidth = timerProgress.interpolate({
    inputRange: [0, 1],
    outputRange: ["0%", "100%"],
  });
  
  return (
    <View style={styles.questionContainer}>
      {/* Header */}
      <View style={styles.questionHeader}>
        <View style={styles.questionBadge}>
          <Text style={styles.questionBadgeText}>
            Vraag {questionNumber}/{totalQuestions}
          </Text>
        </View>
        
        {/* Timer */}
        <View style={styles.timerContainer}>
          <View style={styles.timerBackground}>
            <Animated.View
              style={[
                styles.timerFill,
                {
                  width: timerWidth,
                  backgroundColor: timeRemaining <= 5 ? "#EF4444" : "#10B981",
                },
              ]}
            />
          </View>
          <Text style={styles.timerText}>{timeRemaining}s</Text>
        </View>
      </View>
      
      {/* Question */}
      <Animated.View
        style={[
          styles.questionBox,
          {
            transform: [
              {
                translateY: questionAnim.interpolate({
                  inputRange: [0, 1],
                  outputRange: [-50, 0],
                }),
              },
            ],
            opacity: questionAnim,
          },
        ]}
      >
        {question.image_url && (
          <Image
            source={{ uri: question.image_url }}
            style={styles.questionImage}
            resizeMode="contain"
          />
        )}
        <Text style={styles.questionText}>{question.question}</Text>
      </Animated.View>
      
      {/* Options Grid */}
      <View style={styles.optionsGrid}>
        {question.options.map((option, index) => {
          const correctIndex = result?.correct_option_index ?? (answerRevealed ? question.options.findIndex(o => o.is_correct) : -1);
          const isCorrect = index === correctIndex;
          const showCorrect = result || answerRevealed;
          const answerCount = result?.answer_counts[index] || 0;
          
          return (
            <Animated.View
              key={index}
              style={[
                styles.optionCard,
                {
                  backgroundColor: showCorrect
                    ? isCorrect
                      ? "#10B981"
                      : `${OPTION_COLORS[index]}80`
                    : OPTION_COLORS[index],
                  transform: [
                    {
                      scale: optionAnims[index].interpolate({
                        inputRange: [0, 1],
                        outputRange: [0.8, 1],
                      }),
                    },
                  ],
                  opacity: optionAnims[index],
                },
              ]}
            >
              <View style={styles.optionShape}>
                <Text style={styles.optionShapeText}>
                  {["▲", "◆", "●", "■"][index]}
                </Text>
              </View>
              <Text style={styles.optionText}>{option.text}</Text>
              
              {result && (
                <View style={styles.answerCount}>
                  <Text style={styles.answerCountText}>{answerCount}</Text>
                </View>
              )}
              
              {showCorrect && isCorrect && (
                <View style={styles.correctBadge}>
                  <Text style={styles.correctBadgeText}>✓</Text>
                </View>
              )}
            </Animated.View>
          );
        })}
      </View>
      
      {/* Answer Progress */}
      {!result && (
        <View style={styles.answeredCount}>
          <Text style={styles.answeredCountText}>
            💬 Antwoorden ontvangen...
          </Text>
        </View>
      )}
    </View>
  );
};

/**
 * Ranking View - Shows current standings
 */
interface RankingViewProps {
  rankings: RankingEntry[];
  quizColor: string;
}

const RankingView: React.FC<RankingViewProps> = ({ rankings, quizColor }) => {
  return (
    <View style={styles.rankingContainer}>
      <Text style={styles.rankingTitle}>🏆 Tussenstand</Text>
      
      <View style={styles.rankingList}>
        {rankings.slice(0, 8).map((entry, index) => {
          const getMedal = (rank: number) => {
            if (rank === 1) return "🥇";
            if (rank === 2) return "🥈";
            if (rank === 3) return "🥉";
            return `#${rank}`;
          };
          
          return (
            <View
              key={entry.participant.id}
              style={[
                styles.rankingRow,
                index < 3 && { backgroundColor: `${quizColor}40` },
              ]}
            >
              <Text style={styles.rankMedal}>{getMedal(entry.rank)}</Text>
              
              {entry.mascotLottie && (
                <View style={styles.rankMascot}>
                  <LottieView
                    source={entry.mascotLottie.source}
                    autoPlay
                    loop
                    style={{ width: 60, height: 60 }}
                  />
                </View>
              )}
              
              <View style={styles.rankInfo}>
                <Text style={styles.rankTeamName}>
                  {entry.participant.team?.name || "Team"}
                </Text>
                <Text style={styles.rankCorrect}>
                  {entry.correct_answers} correct
                </Text>
              </View>
              
              <Text style={styles.rankScore}>{entry.total_score}</Text>
            </View>
          );
        })}
      </View>
    </View>
  );
};

/**
 * Final Results View - Shows podium with confetti
 */
interface FinalResultsViewProps {
  rankings: RankingEntry[];
  quizName: string;
  quizColor: string;
}

const FinalResultsView: React.FC<FinalResultsViewProps> = ({
  rankings,
  quizName,
  quizColor,
}) => {
  const first = rankings[0];
  const second = rankings[1];
  const third = rankings[2];
  
  return (
    <View style={styles.finalContainer}>
      {/* Confetti Animation */}
      <View style={styles.confettiContainer}>
        <LottieView
          source={getLottieSource("confetti")}
          autoPlay
          loop={false}
          style={styles.confettiLottie}
        />
      </View>
      
      <Text style={styles.finalTitle}>🎉 {quizName} Afgerond!</Text>
      
      {/* Podium */}
      <View style={styles.podium}>
        {/* Second Place */}
        {second && (
          <View style={styles.podiumSecond}>
            {second.mascotLottie && (
              <LottieView
                source={second.mascotLottie.source}
                autoPlay
                loop
                style={styles.podiumMascot}
              />
            )}
            <Text style={styles.podiumTeamName}>{second.participant.team?.name}</Text>
            <View style={[styles.podiumBlock, styles.podiumBlockSecond]}>
              <Text style={styles.podiumPlace}>🥈</Text>
              <Text style={styles.podiumScore}>{second.total_score}</Text>
            </View>
          </View>
        )}
        
        {/* First Place */}
        {first && (
          <View style={styles.podiumFirst}>
            {first.mascotLottie && (
              <LottieView
                source={first.mascotLottie.source}
                autoPlay
                loop
                style={styles.podiumMascotFirst}
              />
            )}
            <Text style={styles.podiumTeamNameFirst}>{first.participant.team?.name}</Text>
            <View style={[styles.podiumBlock, styles.podiumBlockFirst]}>
              <Text style={styles.podiumPlaceFirst}>🥇</Text>
              <Text style={styles.podiumScoreFirst}>{first.total_score}</Text>
            </View>
          </View>
        )}
        
        {/* Third Place */}
        {third && (
          <View style={styles.podiumThird}>
            {third.mascotLottie && (
              <LottieView
                source={third.mascotLottie.source}
                autoPlay
                loop
                style={styles.podiumMascot}
              />
            )}
            <Text style={styles.podiumTeamName}>{third.participant.team?.name}</Text>
            <View style={[styles.podiumBlock, styles.podiumBlockThird]}>
              <Text style={styles.podiumPlace}>🥉</Text>
              <Text style={styles.podiumScore}>{third.total_score}</Text>
            </View>
          </View>
        )}
      </View>
      
      {/* Other Rankings */}
      {rankings.length > 3 && (
        <View style={styles.otherRankings}>
          {rankings.slice(3, 8).map((entry) => (
            <View key={entry.participant.id} style={styles.otherRankRow}>
              <Text style={styles.otherRankPlace}>#{entry.rank}</Text>
              <Text style={styles.otherRankTeam}>{entry.participant.team?.name}</Text>
              <Text style={styles.otherRankScore}>{entry.total_score} pts</Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

/**
 * Styles
 */
const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    height: "100%",
  } as ViewStyle,
  gradient: {
    flex: 1,
    width: "100%",
  } as ViewStyle,
  statusBar: {
    position: "absolute",
    top: 20,
    right: 20,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.3)",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    zIndex: 100,
  } as ViewStyle,
  connectionDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 8,
  } as ViewStyle,
  statusText: {
    color: "#FFFFFF",
    fontSize: 12,
  } as TextStyle,
  errorText: {
    color: "#FFFFFF",
    fontSize: 24,
    textAlign: "center",
    padding: 40,
  } as TextStyle,
  loadingText: {
    color: "#FFFFFF",
    fontSize: 24,
    marginTop: 20,
  } as TextStyle,
  
  // Lobby
  lobbyContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 40,
  } as ViewStyle,
  quizTitle: {
    color: "#FFFFFF",
    fontSize: 64,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 10,
  } as TextStyle,
  quizSubtitle: {
    color: "rgba(255,255,255,0.7)",
    fontSize: 28,
    marginBottom: 60,
  } as TextStyle,
  teamsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 30,
    maxWidth: 1200,
  } as ViewStyle,
  teamCard: {
    width: 200,
    padding: 24,
    borderRadius: 24,
    backgroundColor: "rgba(255,255,255,0.1)",
    alignItems: "center",
    borderWidth: 3,
    borderColor: "transparent",
  } as ViewStyle,
  teamCardReady: {
    borderColor: "#10B981",
    backgroundColor: "rgba(16,185,129,0.2)",
  } as ViewStyle,
  mascotContainer: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: "rgba(255,255,255,0.2)",
    overflow: "hidden",
    marginBottom: 16,
  } as ViewStyle,
  mascotLottie: {
    width: 120,
    height: 120,
  } as ViewStyle,
  teamName: {
    color: "#FFFFFF",
    fontSize: 22,
    fontWeight: "bold",
    textAlign: "center",
  } as TextStyle,
  readyBadge: {
    marginTop: 12,
    backgroundColor: "#10B981",
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 20,
  } as ViewStyle,
  readyText: {
    color: "#FFFFFF",
    fontWeight: "bold",
  } as TextStyle,
  readyCounter: {
    marginTop: 60,
    backgroundColor: "rgba(255,255,255,0.1)",
    paddingHorizontal: 40,
    paddingVertical: 20,
    borderRadius: 30,
  } as ViewStyle,
  readyCounterText: {
    color: "#FFFFFF",
    fontSize: 28,
    fontWeight: "bold",
  } as TextStyle,
  
  // Question
  questionContainer: {
    flex: 1,
    padding: 40,
  } as ViewStyle,
  questionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 30,
  } as ViewStyle,
  questionBadge: {
    backgroundColor: "rgba(255,255,255,0.2)",
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 30,
  } as ViewStyle,
  questionBadgeText: {
    color: "#FFFFFF",
    fontSize: 20,
    fontWeight: "bold",
  } as TextStyle,
  timerContainer: {
    flexDirection: "row",
    alignItems: "center",
  } as ViewStyle,
  timerBackground: {
    width: 200,
    height: 20,
    backgroundColor: "rgba(255,255,255,0.2)",
    borderRadius: 10,
    overflow: "hidden",
    marginRight: 16,
  } as ViewStyle,
  timerFill: {
    height: "100%",
    borderRadius: 10,
  } as ViewStyle,
  timerText: {
    color: "#FFFFFF",
    fontSize: 32,
    fontWeight: "bold",
    width: 60,
  } as TextStyle,
  questionBox: {
    backgroundColor: "#FFFFFF",
    borderRadius: 24,
    padding: 40,
    marginBottom: 40,
    alignItems: "center",
  } as ViewStyle,
  questionImage: {
    width: 300,
    height: 200,
    backgroundColor: "#E5E7EB",
    borderRadius: 16,
    marginBottom: 20,
  } as ImageStyle,
  questionText: {
    color: "#1F2937",
    fontSize: 36,
    fontWeight: "bold",
    textAlign: "center",
  } as TextStyle,
  optionsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 20,
    justifyContent: "center",
  } as ViewStyle,
  optionCard: {
    minWidth: 400,
    padding: 30,
    borderRadius: 20,
    flexDirection: "row",
    alignItems: "center",
  } as ViewStyle,
  optionShape: {
    width: 50,
    height: 50,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 20,
  } as ViewStyle,
  optionShapeText: {
    color: "#FFFFFF",
    fontSize: 32,
  } as TextStyle,
  optionText: {
    color: "#FFFFFF",
    fontSize: 28,
    fontWeight: "bold",
    flex: 1,
  } as TextStyle,
  answerCount: {
    backgroundColor: "rgba(0,0,0,0.3)",
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 20,
  } as ViewStyle,
  answerCountText: {
    color: "#FFFFFF",
    fontSize: 24,
    fontWeight: "bold",
  } as TextStyle,
  correctBadge: {
    position: "absolute",
    top: 10,
    right: 10,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#FFFFFF",
    alignItems: "center",
    justifyContent: "center",
  } as ViewStyle,
  correctBadgeText: {
    color: "#10B981",
    fontSize: 24,
    fontWeight: "bold",
  } as TextStyle,
  answeredCount: {
    marginTop: 30,
    alignItems: "center",
  } as ViewStyle,
  answeredCountText: {
    color: "rgba(255,255,255,0.7)",
    fontSize: 24,
  } as TextStyle,
  
  // Rankings
  rankingContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 40,
  } as ViewStyle,
  rankingTitle: {
    color: "#FFFFFF",
    fontSize: 56,
    fontWeight: "bold",
    marginBottom: 40,
  } as TextStyle,
  rankingList: {
    width: "100%",
    maxWidth: 800,
    gap: 16,
  } as ViewStyle,
  rankingRow: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(255,255,255,0.1)",
    padding: 20,
    borderRadius: 20,
  } as ViewStyle,
  rankMedal: {
    fontSize: 36,
    width: 60,
    textAlign: "center",
  } as TextStyle,
  rankMascot: {
    width: 60,
    height: 60,
    marginRight: 20,
  } as ViewStyle,
  rankInfo: {
    flex: 1,
  } as ViewStyle,
  rankTeamName: {
    color: "#FFFFFF",
    fontSize: 28,
    fontWeight: "bold",
  } as TextStyle,
  rankCorrect: {
    color: "rgba(255,255,255,0.7)",
    fontSize: 16,
  } as TextStyle,
  rankScore: {
    color: "#F59E0B",
    fontSize: 36,
    fontWeight: "bold",
  } as TextStyle,
  
  // Final Results
  finalContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 40,
  } as ViewStyle,
  confettiContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  } as ViewStyle,
  confettiLottie: {
    width: "100%",
    height: "100%",
  } as ViewStyle,
  finalTitle: {
    color: "#FFFFFF",
    fontSize: 56,
    fontWeight: "bold",
    marginBottom: 60,
    textAlign: "center",
    zIndex: 10,
  } as TextStyle,
  podium: {
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "center",
    gap: 40,
    marginBottom: 40,
    zIndex: 10,
  } as ViewStyle,
  podiumFirst: {
    alignItems: "center",
  } as ViewStyle,
  podiumSecond: {
    alignItems: "center",
  } as ViewStyle,
  podiumThird: {
    alignItems: "center",
  } as ViewStyle,
  podiumMascot: {
    width: 100,
    height: 100,
    marginBottom: 10,
  } as ViewStyle,
  podiumMascotFirst: {
    width: 140,
    height: 140,
    marginBottom: 10,
  } as ViewStyle,
  podiumTeamName: {
    color: "#FFFFFF",
    fontSize: 22,
    fontWeight: "bold",
    marginBottom: 10,
  } as TextStyle,
  podiumTeamNameFirst: {
    color: "#FFFFFF",
    fontSize: 28,
    fontWeight: "bold",
    marginBottom: 10,
  } as TextStyle,
  podiumBlock: {
    width: 150,
    alignItems: "center",
    justifyContent: "center",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  } as ViewStyle,
  podiumBlockFirst: {
    height: 180,
    backgroundColor: "#F59E0B",
  } as ViewStyle,
  podiumBlockSecond: {
    height: 130,
    backgroundColor: "#9CA3AF",
  } as ViewStyle,
  podiumBlockThird: {
    height: 100,
    backgroundColor: "#CD7F32",
  } as ViewStyle,
  podiumPlace: {
    fontSize: 48,
    marginBottom: 10,
  } as TextStyle,
  podiumPlaceFirst: {
    fontSize: 64,
    marginBottom: 10,
  } as TextStyle,
  podiumScore: {
    color: "#FFFFFF",
    fontSize: 28,
    fontWeight: "bold",
  } as TextStyle,
  podiumScoreFirst: {
    color: "#FFFFFF",
    fontSize: 36,
    fontWeight: "bold",
  } as TextStyle,
  otherRankings: {
    backgroundColor: "rgba(255,255,255,0.1)",
    borderRadius: 20,
    padding: 20,
    zIndex: 10,
  } as ViewStyle,
  otherRankRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
    paddingHorizontal: 20,
    gap: 20,
  } as ViewStyle,
  otherRankPlace: {
    color: "rgba(255,255,255,0.7)",
    fontSize: 20,
    width: 40,
  } as TextStyle,
  otherRankTeam: {
    color: "#FFFFFF",
    fontSize: 20,
    flex: 1,
  } as TextStyle,
  otherRankScore: {
    color: "#F59E0B",
    fontSize: 20,
    fontWeight: "bold",
  } as TextStyle,
  
  // Countdown View Styles
  countdownContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 40,
  } as ViewStyle,
  countdownSubtitle: {
    color: "rgba(255,255,255,0.7)",
    fontSize: 28,
    marginBottom: 20,
  } as TextStyle,
  countdownTitle: {
    color: "#FFFFFF",
    fontSize: 48,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 60,
    textShadowColor: "rgba(0,0,0,0.3)",
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  } as TextStyle,
  countdownCircle: {
    width: 250,
    height: 250,
    borderRadius: 125,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.3,
    shadowRadius: 20,
  } as ViewStyle,
  countdownNumber: {
    color: "#FFFFFF",
    fontSize: 120,
    fontWeight: "900",
    textShadowColor: "rgba(0,0,0,0.2)",
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  } as TextStyle,
  
  // Results View Styles
  resultsContainer: {
    flex: 1,
    padding: 40,
    alignItems: "center",
  } as ViewStyle,
  resultsSubtitle: {
    color: "rgba(255,255,255,0.7)",
    fontSize: 24,
    marginBottom: 20,
  } as TextStyle,
  correctAnswerBox: {
    backgroundColor: "#10B981",
    borderRadius: 24,
    padding: 30,
    alignItems: "center",
    marginBottom: 30,
    width: "100%",
    maxWidth: 800,
  } as ViewStyle,
  correctAnswerLabel: {
    color: "#FFFFFF",
    fontSize: 24,
    fontWeight: "600",
    marginBottom: 10,
  } as TextStyle,
  correctAnswerText: {
    color: "#FFFFFF",
    fontSize: 36,
    fontWeight: "bold",
    textAlign: "center",
  } as TextStyle,
  fastestBox: {
    backgroundColor: "rgba(245,158,11,0.3)",
    borderWidth: 3,
    borderColor: "#F59E0B",
    borderRadius: 24,
    padding: 24,
    marginBottom: 30,
    width: "100%",
    maxWidth: 600,
  } as ViewStyle,
  fastestLabel: {
    color: "#F59E0B",
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 16,
  } as TextStyle,
  fastestInfo: {
    flexDirection: "row",
    alignItems: "center",
  } as ViewStyle,
  fastestMascot: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: "rgba(255,255,255,0.2)",
    overflow: "hidden",
    marginRight: 20,
  } as ViewStyle,
  fastestDetails: {
    flex: 1,
  } as ViewStyle,
  fastestTeamName: {
    color: "#FFFFFF",
    fontSize: 28,
    fontWeight: "bold",
  } as TextStyle,
  fastestTime: {
    color: "rgba(255,255,255,0.7)",
    fontSize: 18,
    marginTop: 4,
  } as TextStyle,
  allResultsBox: {
    width: "100%",
    maxWidth: 900,
  } as ViewStyle,
  allResultsTitle: {
    color: "#FFFFFF",
    fontSize: 28,
    fontWeight: "bold",
    marginBottom: 20,
  } as TextStyle,
  resultsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
    justifyContent: "center",
  } as ViewStyle,
  resultCard: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    borderRadius: 16,
    minWidth: 280,
  } as ViewStyle,
  resultIndicator: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  } as ViewStyle,
  resultIndicatorText: {
    color: "#FFFFFF",
    fontSize: 24,
    fontWeight: "bold",
  } as TextStyle,
  resultMascot: {
    width: 50,
    height: 50,
    borderRadius: 25,
    overflow: "hidden",
    marginRight: 12,
  } as ViewStyle,
  resultInfo: {
    flex: 1,
  } as ViewStyle,
  resultTeamName: {
    color: "#FFFFFF",
    fontSize: 20,
    fontWeight: "600",
  } as TextStyle,
  resultTime: {
    color: "rgba(255,255,255,0.7)",
    fontSize: 14,
    marginTop: 2,
  } as TextStyle,
  resultPoints: {
    fontSize: 28,
    fontWeight: "bold",
  } as TextStyle,

  // Lobby connection dot
  lobbyConnectionDot: {
    position: "absolute",
    top: 12,
    right: 12,
    width: 14,
    height: 14,
    borderRadius: 7,
    borderWidth: 2,
    borderColor: "rgba(255,255,255,0.3)",
    zIndex: 10,
  } as ViewStyle,

  // Pause overlay
  pauseOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.8)",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
  } as ViewStyle,
  pauseCard: {
    backgroundColor: "#1E293B",
    borderRadius: 32,
    padding: 60,
    alignItems: "center",
    maxWidth: 700,
  } as ViewStyle,
  pauseIcon: {
    fontSize: 80,
    marginBottom: 20,
  } as TextStyle,
  pauseTitle: {
    color: "#FFFFFF",
    fontSize: 48,
    fontWeight: "bold",
    marginBottom: 16,
  } as TextStyle,
  pauseMessage: {
    color: "#F59E0B",
    fontSize: 32,
    fontWeight: "600",
    textAlign: "center",
    marginBottom: 20,
  } as TextStyle,
  pauseHint: {
    color: "rgba(255,255,255,0.6)",
    fontSize: 22,
    textAlign: "center",
  } as TextStyle,
});

